import { distinct, ensureArray } from "../common/utils/arrayUtilities";
import { decodeJWT } from "../common/utils/generalUtils.js"

const specialRoles = ['administrator','dispatcher','stats','sitrep'];

export const credentialsFactory = token => {
    const tokenData = token ? decodeJWT(token) : ({ role: [], sitrep_access: [], log_access: [] });

    // each role has a schema of { type, site, level, access }
    // access: 0 = read-only, 1 = read-write, 2 = log-only
    // token has fields: role, log_access, sitrep_access
    const perms = {
        role: tokenData.role,
        sitrep_access: tokenData.sitrep_access,
        log_access: tokenData.log_access  
    }
    const checkPerms = (r, perms, accessFilter) => {
        if (typeof r === 'string') {
            r = { type: r }; 
        }
        return ensureArray(perms).some(p => (!r.type || !p.type || p.type === r.type) && (!r.site || !p.site || p.site === r.site) && (!r.level || !p.level || p.level === r.level) && accessFilter(p.access))
    }
    const checkAccess = (r, perms, accessFilter) => {
        if (typeof r === 'string') {
            r = { type: r };
        }
        if (specialRoles.some(sp => r.type === sp)) {
            return false;
        }
        perms = ensureArray(perms);
        perms = perms.filter(p => specialRoles.every(s => p.type !== s));
        
        return checkPerms(r, perms, accessFilter)
    }
    const fullFilter = a => {
        return a === 1
    };
    const logFilter = a => a === 1 || a === 2;
    const readFilter = _ => true;
    const makeAccessObject = (perms, f, isSuper) => ({
        hasFull: roles => ensureArray(roles).some(r => perms.role?.some(p => isSuper(p) && fullFilter(p.access)) || checkAccess(r, perms[f], fullFilter)),
        hasLog: roles => ensureArray(roles).some(r => perms.role?.some(p => isSuper(p) && logFilter(p.access)) || checkAccess(r, perms[f], logFilter)),
        has: roles => ensureArray(roles).some(r => perms.role?.some(p => isSuper(p) && readFilter(p.access)) || checkAccess(r, perms[f], readFilter)),
        none: () => !perms[f] || perms[f].length === 0,
        accessTriples: (clientConfig, access) => {
            let filter;
            switch (access) {
                case 'full':
                    filter = fullFilter;
                    break;
                case 'log':
                    filter = logFilter;
                    break;
                case 'read':
                    filter = readFilter;
                    break;
                default:
                    throw new Error('Unknown access for triples ' + access);
            }
            if (perms.role?.some(p => isSuper(p) && filter(p.access))) {
                return null;
            }
            const triples = perms[f].filter(p => filter(p.access));
            if (triples.some(p => p.type == null || (clientConfig.enableSites && p.site == null) || (clientConfig.enableLevels && p.level == null))) {
                return null;
            }
            return distinct(triples.map(t => ({ ...t, id: `${clientConfig.enableSites ? t.site : ''}_${clientConfig.enableLevels ? t.level : ''}_${t.type}`})), 'id').map(({ site, level, type}) => ({ site, level, type }));
        }
    })
    return {
        strict: {
            hasFull: roles => ensureArray(roles).some(r => checkPerms(r, perms.role, a => a === 1)),
            hasLog: roles => ensureArray(roles).some(r => checkPerms(r, perms.role, a => a === 1 || a === 2)),
            has: roles => ensureArray(roles).some(r => checkPerms(r, perms.role, _ => true)),
            none: () => !perms.role || perms.role.length === 0
        },
        access: makeAccessObject(perms, 'role', p => p.type === 'administrator' || p.type === 'dispatcher' || p.type === 'stats'),
        log: makeAccessObject(perms, 'log_access', p => p.type === 'administrator' || p.type === 'dispatcher'),
        sitrep: makeAccessObject(perms, 'sitrep_access', p => p.type === 'administrator' || p.type === 'sitrep'),
        username: tokenData.username,
        email: perms.email,
        kind: perms.kind
    }
}

export const validateAccess = (credentials, access, { level = 1, area ='access' } = {}) => {
    let check;
    let accessFilter;
    switch (level) {
        case 1:
            check = 'hasFull';
            accessFilter = a => a.access === undefined || a.access === null || a.access == 1;
            break;
        case 2:
            check = 'hasLog';
            accessFilter = a => a.access === undefined || a.access === null || a.access > 0;
            break;
        default:
            check = 'has';
            accessFilter = a => a.access === undefined || a.access === null || a.access >= 0;
            break;
    }
    return !access || credentials[area][check](ensureArray(access).filter(accessFilter)) || credentials[area][check]('administrator');
}

export const validateTransition = (credentials, access, src, { srcField = 'src', ...opts} = {}) => {
    return !access || credentials.access.hasFull('administrator') || ensureArray(access).some(perm => validateAccess(credentials, perm, { ...opts }) && (perm[srcField] === undefined || perm[srcField] == src || (Array.isArray(perm[srcField]) && perm[srcField].includes(src))));
} 